import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorListItem,
  List,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useIdempotencyKey } from '../../../../hooks/useIdempotencyKey';
import { useCallback, useEffect } from 'react';
import { ApiMember } from '../../../../openapi';
import { useDeleteBusinessMember } from '../../../../api/members';
import { MemberListItem } from './MemberListItem';

export function DeleteMemberDialog({
  member,
  onCancel,
  onSuccess,
}: {
  member: ApiMember | undefined;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const deleteMutation = useDeleteBusinessMember({ idempotencyKey });

  const handleCancel = useCallback(() => {
    deleteMutation.reset();
    onCancel();
  }, [deleteMutation, onCancel]);

  const handleConfirm = useCallback(() => {
    if (member) {
      deleteMutation.mutate({ members: [member.customer.id] });
    }
  }, [member, deleteMutation]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      onSuccess();
      deleteMutation.reset();
    }
  }, [deleteMutation, onSuccess]);

  return (
    <Dialog open={Boolean(member)} fullWidth maxWidth="sm" onClose={handleCancel}>
      <DialogTitle>
        {t(`Are you sure you want to revoke {{member}} access?`, {
          member: member?.customer.name ?? member?.customer.username,
        })}
      </DialogTitle>
      <DialogContent>
        <List>
          <MemberListItem member={member} />
          {deleteMutation.isError ? <ErrorListItem error={deleteMutation.error} /> : null}
        </List>
      </DialogContent>
      <DialogActions>
        <Button autoFocus color="secondary" onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button color="error" loading={deleteMutation.isPending} onClick={handleConfirm}>
          {t('Revoke access')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
